<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div class="card shadow mb-3">

    <!-- Delete Resource Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" role="dialog" aria-labelledby="deleteModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h2>Delete Resource</h2>
            <button type="button btn-lg" class="close" data-dismiss="modal" aria-label="Cancel">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p v-if="existingData.is_template" class="lead">If you proceed, all data about the template will be deleted. This action is irreversible.</p>
            <p v-else class="lead">If you proceed, the resource will be unpublished from any registry in which it is published and all data about the resource will be deleted.</p>
            <div class="d-flex gap-2 pt-2" style="gap: 10px;">
              <button type="button" class="btn btn-sm btn-danger" data-dismiss="modal">Cancel</button>
              <form method="POST" :action="baseUrls.destroyUrl" style="display:inline;">
                <input type="hidden" name="_token" :value="csrf_token">
                <input type="hidden" name="_method" value="DELETE">
                <button type="submit" class="btn btn-sm btn-registry pull-left">Delete</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transfer Resource Modal -->
    <div class="modal fade" id="transferModal" tabindex="-1" role="dialog" aria-labelledby="transferModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
        <div class="modal-content">
          <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
            <button type="button" class="close" data-dismiss="modal" aria-label="Cancel" style="position: static; margin-left: auto;">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

            <div class="modal-body" style="padding: 0 30px 30px 30px;">
              <h4 style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Transfer {{ typeOfResource.charAt(0).toUpperCase() + typeOfResource.slice(1) }} Ownership</h4>
              <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">By clicking "Request Transfer", an email will be sent to the new owner inviting them to accept ownership of this {{ typeOfResource }}. <strong>You remain the owner until they accept</strong>, and you can cancel the request at any time before then. Only one transfer request can be pending at a time. The new owner must have an existing IoTPI account.</p>

              <form id="transferForm" method="POST" enctype="multipart/form-data" :action="baseUrls.transferUrl">
                <input type="hidden" name="_token" :value="csrf_token">
                <input type="hidden" name="resource" :value="existingData.id">

                <div class="form-group" style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;">
                  <label for="new_owner_email" style="font-weight: bold; font-size: 16px; color: #333; white-space: nowrap; padding-top: 8px; flex-shrink: 0;">New Owner Email</label>
                  <div style="flex-grow: 1;">
                    <input type="email" id="new_owner_email" name="new_owner_email" placeholder="" required="required" class="form-control" @input="validateEmail" style="margin-bottom: 8px;">
                    <small id="new_owner_email-help" class="form-text text-muted">Note: The email address of the new owner must be what they use for their IoTPI account</small>
                  </div>
                </div>

                <div class="d-flex gap-2 pt-2" style="gap: 10px;">
                  <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
                  <button type="submit" id="transfer-form-button" class="btn btn-registry" :disabled="!isEmailValid" :style="{opacity: isEmailValid ? 1 : 0.6, cursor: isEmailValid ? 'pointer' : 'not-allowed'}">Request Transfer</button>
                </div>
              </form>

            </div>

        </div>
      </div>
    </div>

    <!-- Cancel Transfer Ownership Request Modal -->
    <div v-if="hasPendingTransfer" class="modal fade" id="cancelTransferModal" tabindex="-1" role="dialog" aria-labelledby="cancelTransferModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
        <div class="modal-content">
          <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" style="position: static; margin-left: auto;">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

            <div class="modal-body" style="padding: 0 30px 30px 30px;">
              <h4 id="cancelTransferModalLabel" style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Cancel Transfer Ownership Request</h4>
              <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">
                A request to transfer ownership of this {{ typeOfResource }} to <strong>{{ ownershipTransfer.pending.newOwnerName }}</strong> ({{ ownershipTransfer.pending.newOwnerEmail }}) was sent on {{ ownershipTransfer.pending.requestedAt }} and has not been accepted yet.
                If it is not accepted it expires on its own on {{ ownershipTransfer.pending.expiresAt }}.
                Canceling now withdraws the invitation immediately; the current owner keeps ownership and a new request can be sent afterwards.
              </p>

              <form method="POST" :action="ownershipTransfer.pending.cancelUrl">
                <input type="hidden" name="_token" :value="csrf_token">
                <div class="d-flex gap-2 pt-2" style="gap: 10px;">
                  <button type="button" class="btn btn-registry" data-dismiss="modal">Keep Request</button>
                  <button type="submit" class="btn btn-danger">Cancel Transfer Request</button>
                </div>
              </form>
            </div>

        </div>
      </div>
    </div>

    <!-- Duplicate Template for Another User Modal (super system admins only) -->
    <div v-if="showDuplicateTemplateFeature" class="modal fade" id="duplicateTemplateModal" tabindex="-1" role="dialog" aria-labelledby="duplicateTemplateModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
        <div class="modal-content">
          <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
            <button type="button" class="close" data-dismiss="modal" aria-label="Cancel" style="position: static; margin-left: auto;">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

            <div class="modal-body" style="padding: 0 30px 30px 30px;">
              <h4 id="duplicateTemplateModalLabel" style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Duplicate Template for Another User</h4>
              <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">By clicking "Duplicate Template", an exact copy of this template, with all of its details, will be created in the account of the user whose email address you enter below. This template is not changed and you remain able to see both. The recipient must have an existing IoTPI account.</p>

              <form id="duplicateTemplateForm" @submit.prevent="submitDuplicateTemplate">
                <div class="form-group" style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;">
                  <label for="duplicate_owner_email" style="font-weight: bold; font-size: 16px; color: #333; white-space: nowrap; padding-top: 8px; flex-shrink: 0;">Recipient Email</label>
                  <div style="flex-grow: 1;">
                    <input type="email" id="duplicate_owner_email" name="new_owner_email" placeholder="" required="required" class="form-control" :class="{ 'is-invalid': duplicateTemplateError }" v-model="duplicateTemplateEmail" @input="duplicateTemplateError = ''" style="margin-bottom: 8px;">
                    <div v-if="duplicateTemplateError" class="invalid-feedback d-block" style="margin-bottom: 8px;">{{ duplicateTemplateError }}</div>
                    <small id="duplicate_owner_email-help" class="form-text text-muted">Note: The email address must be what the recipient uses for their IoTPI account</small>
                  </div>
                </div>

                <div class="d-flex gap-2 pt-2" style="gap: 10px;">
                  <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
                  <button type="submit" id="duplicate-template-form-button" class="btn btn-registry" :disabled="!isDuplicateEmailValid || duplicateTemplateInProgress" :style="{opacity: (isDuplicateEmailValid && !duplicateTemplateInProgress) ? 1 : 0.6, cursor: (isDuplicateEmailValid && !duplicateTemplateInProgress) ? 'pointer' : 'not-allowed'}">{{ duplicateTemplateInProgress ? 'Duplicating...' : 'Duplicate Template' }}</button>
                </div>
              </form>

            </div>

        </div>
      </div>
    </div>

    <!-- Copy resource to template modal -->
    <div v-if="showCopyAsTemplateFeature" class="modal fade" id="copyToTemplateModal" tabindex="-1" role="dialog" aria-labelledby="copyToTemplateModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title" id="copyToTemplateModalLabel">Copy to Template</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body pt-2">
            <p>Enter a name for the template in the field below. We recommend that you choose a name distinct from your resource name to avoid confusion later.</p>
            <div class="form-group mb-0">
              <input
                type="text"
                class="form-control"
                id="summaryTemplateCopyName"
                v-model="templateCopyName"
                maxlength="40"
                :class="{ 'is-invalid': templateCopyNameError }"
                placeholder="Template name"
                @keyup.enter.prevent="submitCopyAsTemplate"
              >
              <div v-if="templateCopyNameError" class="invalid-feedback d-block">{{ templateCopyNameError }}</div>
            </div>
          </div>
          <div class="modal-footer border-0 justify-content-start pt-0">
            <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary ml-2" :disabled="templateCopyInProgress" @click="submitCopyAsTemplate">{{ templateCopyInProgress ? 'Copying...' : 'Copy' }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body">
      <div class="card-text">
        <center>
          <div v-if="templateCopySuccessMessage" class="alert alert-success text-left" role="alert">
            {{ templateCopySuccessMessage }}
            <template v-if="templateCopyId">
              <a :href="'/resource/' + templateCopyId" target="_blank" rel="noopener noreferrer">Click here</a> to view the template in a new tab.
            </template>
          </div>
          <div v-if="duplicateTemplateSuccessMessage" class="alert alert-success text-left" role="alert">
            {{ duplicateTemplateSuccessMessage }}
            <template v-if="duplicateTemplateId">
              <a :href="'/resource/' + duplicateTemplateId" target="_blank" rel="noopener noreferrer">Click here</a> to view the duplicated template in a new tab.
            </template>
          </div>

          <!-- ownership transfer flash messages -->
          <div v-if="ownershipTransfer && ownershipTransfer.status" class="alert alert-success text-left" role="alert">{{ ownershipTransfer.status }}</div>
          <div v-if="ownershipTransfer && ownershipTransfer.error" class="alert alert-danger text-left" role="alert">{{ ownershipTransfer.error }}</div>

          <!-- invitation addressed to the current user -->
          <div v-if="incomingTransfer" class="alert alert-info text-left" role="alert">
            <p class="mb-2">
              <strong>{{ incomingTransfer.requestedByName }}</strong> ({{ incomingTransfer.requestedByEmail }}) has invited you to become the owner of this {{ incomingTransfer.entityLabel }}<span v-if="!incomingTransfer.requestedByIsCurrentOwner">, currently owned by <strong>{{ incomingTransfer.currentOwnerName }}</strong></span>.
              Nothing changes until you accept. This invitation expires on <strong>{{ incomingTransfer.expiresAt }}</strong>.
            </p>
            <div class="d-flex flex-wrap align-items-center" style="gap: 10px;">
              <form method="POST" :action="incomingTransfer.acceptUrl" style="display:inline;">
                <input type="hidden" name="_token" :value="csrf_token">
                <button type="submit" class="btn btn-sm btn-registry">Accept Ownership</button>
              </form>
              <form method="POST" :action="incomingTransfer.declineUrl" style="display:inline;">
                <input type="hidden" name="_token" :value="csrf_token">
                <button type="submit" class="btn btn-sm btn-danger">Decline</button>
              </form>
              <a :href="incomingTransfer.reviewUrl" class="small">Review request details</a>
            </div>
          </div>

          <!-- pending request sent from this entity (visible to the owner / requester / system admins) -->
          <div v-if="canTransfer && hasPendingTransfer" class="alert alert-warning text-left" role="alert">
            <strong>Ownership transfer pending.</strong>
            A request to transfer ownership of this {{ typeOfResource }} to <strong>{{ ownershipTransfer.pending.newOwnerName }}</strong> ({{ ownershipTransfer.pending.newOwnerEmail }}) was sent on {{ ownershipTransfer.pending.requestedAt }}<span v-if="!ownershipTransfer.pending.requestedByIsCurrentOwner"> by {{ ownershipTransfer.pending.requestedByName }}</span>.
            The current owner remains the owner until they accept. The request expires on {{ ownershipTransfer.pending.expiresAt }} if not accepted. <a :href="ownershipTransfer.pending.reviewUrl">View request</a>
          </div>
          <h1>{{ existingData.name }}</h1>
          <p v-if="!existingData.is_template"><small>Resource ID # {{ existingData.id }}</small></p>
          <p v-else><small>Template ID # {{ existingData.id }}</small></p>
          <p class="lead text-muted">{{ existingData.description }}<br>
          <div v-if="existingData.image"><img :src="existingData.image"></div>
          <div class="img-thumb" v-else>Image Not Provided</div></p>
          <!-- <h5>CONTRIBUTOR:&nbsp;<a :href="baseUrls.userBaseUrl + existingData.user_id">{{ existingData.contributor }}</a></h5> -->
          <h6>CONTRIBUTOR:&nbsp; {{ existingData.contributor }} </h6>

          <button v-if="(existingData.isOwner || permissions.currentPermissions.SYS_DELETE_RESOURCE )" type="button" class="btn btn-danger" data-toggle="modal" data-target="#deleteModal">Delete</button>
          <a :href="baseUrls.editUrl" v-if="existingData.isOwner" type="button" class="btn btn-registry">Edit</a>
          <button v-if="canTransfer && !hasPendingTransfer" type="button" class="btn btn-registry" data-toggle="modal" data-target="#transferModal">Transfer Ownership</button>
          <button v-if="canTransfer && hasPendingTransfer" type="button" class="btn btn-warning" data-toggle="modal" data-target="#cancelTransferModal">Cancel Transfer Ownership Request</button>
          <button v-if="showDuplicateTemplateFeature" type="button" class="btn btn-registry" data-toggle="modal" data-target="#duplicateTemplateModal" @click="openDuplicateTemplateModal">Duplicate for Another User</button>
          <a :href="baseUrls.publishUrl" v-if="(existingData.isOwner || permissions.currentPermissions.SYS_DELETE_RESOURCE) && !existingData.is_template && existingData.isComplete" type="button" class="btn btn-registry">Publish</a>
          <button v-if="showCopyAsTemplateFeature" type="button" class="btn btn-registry" data-toggle="modal" data-target="#copyToTemplateModal" @click="openCopyAsTemplateModal">Copy as Template</button>
          <a :href="baseUrls.loadTemplateUrl" v-if="existingData.isOwner && existingData.is_template" type="button" class="btn btn-success">Create Resource Instance</a>
        </center>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
      props: {
        existingData: [Object, Array],
        isTemplate: [Boolean, Number],
        permissions: Object,
        csrf_token: String,
        // { pending: {...}|null, incoming: {...}|null, error: '', status: '' } - see App\Support\OwnershipTransfers::viewPayload()
        ownershipTransfer: {
          type: Object,
          default: () => ({ pending: null, incoming: null, error: '', status: '' })
        },
      },
      data: function() {
        return {
          isEmailValid: false,
          templateCopyName: '',
          templateCopyNameError: '',
          templateCopyInProgress: false,
          templateCopySuccessMessage: '',
          templateCopyId: null,
          duplicateTemplateEmail: '',
          duplicateTemplateError: '',
          duplicateTemplateInProgress: false,
          duplicateTemplateSuccessMessage: '',
          duplicateTemplateId: null,
          baseUrls: {
            resourceBaseUrl: '/resource/',
            irrBaseUrl: '/irr/',
            userBaseUrl: '/user/',
            destroyUrl: '/resource/' + this.existingData.id + '/destroy',
            editUrl: '/resource/' + this.existingData.id + '/edit/',
            publishUrl: '/resource/' + this.existingData.id + '/publish/',
            transferUrl: '/resource/transfer',
            duplicateTemplateUrl: '/template/duplicate-for-user',
            loadTemplateUrl: '/template/' + this.existingData.id + '/load/',
          },
        }
      },
      methods: {
        validateEmail() {
          const email = document.getElementById('new_owner_email').value;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          this.isEmailValid = emailRegex.test(email);
        },
        openCopyAsTemplateModal() {
          this.templateCopyName = '';
          this.templateCopyNameError = '';
          this.templateCopySuccessMessage = '';
          this.templateCopyId = null;
        },
        submitCopyAsTemplate() {
          var templateName = (this.templateCopyName || '').trim();
          if (!templateName) {
            this.templateCopyNameError = 'Please enter a name for the template.';
            return;
          }

          this.templateCopyNameError = '';
          this.templateCopyInProgress = true;
          var _this = this;

          $.ajax({
            url: '/resource/copy-as-template',
            method: 'POST',
            data: {
              _templateCopyName: templateName,
              _sourceResourceId: this.existingData.id,
            },
            headers: { 'X-CSRF-TOKEN': this.csrf_token },
            success: function(response) {
              _this.templateCopyInProgress = false;
              $('#copyToTemplateModal').modal('hide');
              _this.templateCopyName = '';
              _this.templateCopySuccessMessage = response.message;
              _this.templateCopyId = response.templateId || null;
            },
            error: function(xhr) {
              _this.templateCopyInProgress = false;
              if (xhr.status === 422 && xhr.responseJSON && xhr.responseJSON.errors) {
                var errors = xhr.responseJSON.errors;
                if (errors._templateCopyName && errors._templateCopyName.length) {
                  _this.templateCopyNameError = errors._templateCopyName[0];
                } else {
                  var firstError = null;
                  for (var field in errors) {
                    if (errors.hasOwnProperty(field) && errors[field] && errors[field].length) {
                      firstError = errors[field][0];
                      break;
                    }
                  }
                  _this.templateCopyNameError = firstError || 'Unable to create template. Please check your resource data.';
                }
              } else {
                _this.templateCopyNameError = 'An error occurred while creating the template. Please try again.';
              }
            }
          });
        },
        openDuplicateTemplateModal() {
          this.duplicateTemplateEmail = '';
          this.duplicateTemplateError = '';
          this.duplicateTemplateInProgress = false;
          this.duplicateTemplateSuccessMessage = '';
          this.duplicateTemplateId = null;
        },
        submitDuplicateTemplate() {
          if (!this.isDuplicateEmailValid || this.duplicateTemplateInProgress) {
            return;
          }

          this.duplicateTemplateError = '';
          this.duplicateTemplateInProgress = true;
          var _this = this;

          $.ajax({
            url: this.baseUrls.duplicateTemplateUrl,
            method: 'POST',
            data: {
              template: this.existingData.id,
              new_owner_email: (this.duplicateTemplateEmail || '').trim(),
            },
            headers: { 'X-CSRF-TOKEN': this.csrf_token },
            success: function(response) {
              _this.duplicateTemplateInProgress = false;
              $('#duplicateTemplateModal').modal('hide');
              _this.duplicateTemplateEmail = '';
              _this.duplicateTemplateSuccessMessage = response.message;
              _this.duplicateTemplateId = response.templateId || null;
            },
            error: function(xhr) {
              _this.duplicateTemplateInProgress = false;
              if (xhr.responseJSON && xhr.responseJSON.errors) {
                var errors = xhr.responseJSON.errors;
                var firstError = null;
                for (var field in errors) {
                  if (errors.hasOwnProperty(field) && errors[field] && errors[field].length) {
                    firstError = errors[field][0];
                    break;
                  }
                }
                _this.duplicateTemplateError = firstError || 'Unable to duplicate the template. Please try again.';
              } else if (xhr.status === 403) {
                _this.duplicateTemplateError = 'You do not have permission to duplicate templates for other users.';
              } else {
                _this.duplicateTemplateError = 'An error occurred while duplicating the template. Please try again.';
              }
            }
          });
        },
      },
      computed: {
        typeOfResource: function () {
          return (this.isTemplate) ? 'template' : 'resource';
        },
        canTransfer: function () {
          return !!(this.existingData.isOwner || (this.permissions && this.permissions.currentPermissions && this.permissions.currentPermissions.SYS_TRANSFER_RESOURCE));
        },
        // a transfer request has been sent and is awaiting the invited user's response
        hasPendingTransfer: function () {
          return !!(this.ownershipTransfer && this.ownershipTransfer.pending);
        },
        // the current user is the one invited to take ownership
        incomingTransfer: function () {
          return (this.ownershipTransfer && this.ownershipTransfer.incoming) ? this.ownershipTransfer.incoming : null;
        },
        showCopyAsTemplateFeature: function () {
          return this.existingData.isOwner && !this.existingData.is_template && !this.isTemplate;
        },
        // Only super system admins (SYS_DUPLICATE_TEMPLATE permission) may duplicate a template
        // into another user's account. Owning the template alone does not show this button.
        showDuplicateTemplateFeature: function () {
          return !!(this.existingData.is_template
            && this.permissions
            && this.permissions.currentPermissions
            && this.permissions.currentPermissions.SYS_DUPLICATE_TEMPLATE);
        },
        isDuplicateEmailValid: function () {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test((this.duplicateTemplateEmail || '').trim());
        },
      },
      mounted() {
        const urlParams = new URLSearchParams(window.location.search);

        //check for 'delete' in query string, display delete modal if applicable
        if(this.permissions.currentPermissions.SYS_DELETE_RESOURCE || this.existingData.isOwner) {
          if(urlParams.get('delete') == 1) {
            $('#deleteModal').modal('show');
          }
        }

        //check for 'transfer' in query string, display the transfer modal (or the cancel modal if a request is already pending) if applicable
        if(this.canTransfer) {
          if(urlParams.get('transfer') == 1) {
            if(this.hasPendingTransfer) {
              $('#cancelTransferModal').modal('show');
            } else {
              $('#transferModal').modal('show');
            }
          }
        } else {
          $('#transferModal').html('');
        }
      },
    }
</script>
<style>
.img-thumb {
  width: 200px;
  height: 200px;
  border: 1px solid;
}
</style>
